import { useState, useMemo } from 'react';
import { Box, Typography, MenuItem, FormControl, Select } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { ThemePalette } from 'mui.theme';
import NotificationsTable from './NotificationsTable';
import { extractReason } from 'views/TirePressure/util';
import { useLocation } from 'react-router-dom';
import type { MakeModelDetails } from 'types/make-model.types';
import type {
  TirePattern,
  TirePatternPosition,
} from 'types/tire-pattern.types';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const TotalNotification = ({
  enableDarkTheme,
  notifications,
  selectedDate,
  selectedAsset,
  suspendNotification,
  makeModelDetails,
  tirePatterns,
  customerId,
}: {
  enableDarkTheme: boolean;
  sortBy: string;
  handleChange: any;
  notifications: any[];
  selectedDate: string | null;
  selectedAsset: {
    bumperId: string;
    deviceId: string;
  } | null;
  suspendNotification: any;
  makeModelDetails: MakeModelDetails[];
  tirePatterns: {
    tirePatterns: TirePattern[];
    tirePatternPositions: TirePatternPosition[];
  };
  customerId: string;
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { t } = useTranslation();
  const location = useLocation();

  const [notificationType, setNotificationType] = useState<string>(
    location?.state?.notificationStatus ?? 'all'
  );
  const handleChange = (event: any) => {
    setNotificationType(event.target.value);
  };

  const filteredNotifications: any = useMemo(() => {
    let notificationList = notifications;
    if (selectedAsset) {
      notificationList = notifications.filter(
        (item) => item.asset?.bumperNumber === selectedAsset.bumperId
      );
    }

    if (notificationType === 'all') {
      return notificationList;
    }
    if (notificationType === 'active') {
      return notificationList.filter((item) => {
        // For leakage notifications, just check isActive
        if (item.source === 'leakage') {
          return item.isActive;
        }
        // For TPMS notifications, exclude 'inflate' type
        return (
          item.isActive && extractReason(item?.rule?.ruleName) !== 'inflate'
        );
      });
    }
    if (notificationType === 'solved') {
      return notificationList.filter((item) => !item.isActive);
    }
    if (notificationType === 'info') {
      // Only TPMS notifications have 'inflate' type
      return notificationList.filter(
        (item) =>
          item.source !== 'leakage' &&
          extractReason(item?.rule?.ruleName) === 'inflate'
      );
    }
  }, [selectedAsset, notifications, notificationType]);

  const fontColor = palette.textPrimary;

  function filterByDate(dataArray: any[], targetDateStr: string) {
    return dataArray.filter((item) => {
      const itemDate = new Date(item.alertStartTs).toISOString().split('T')[0];
      return itemDate === targetDateStr;
    });
  }

  let dataToDisplay = filteredNotifications;

  if (selectedDate) {
    dataToDisplay = filterByDate(dataToDisplay, selectedDate);
  }

  return (
    <Box
      sx={{
        marginTop: '10px',
        borderRadius: '8px',
        border: '1px solid #2F445E',
        overflow: 'scroll',
        marginBottom: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '10px',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '700',
              color: fontColor,
            }}
          >
            {t('notification_history_2')}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: '500',
              color: ThemePalette.typography.grey,
              marginLeft: '20px',
              marginRight: '20px',
            }}
          >
            {t('show')}
          </Typography>
          <FormControl sx={{ minWidth: 120 }}>
            <Select
              value={notificationType}
              onChange={handleChange}
              displayEmpty
              IconComponent={KeyboardArrowDown}
              sx={{
                height: '40px',
                width: '206px',
                fontSize: '14px',
                color: fontColor,
                border: '1px solid #26364C',
                '& .MuiSelect-select': {
                  paddingLeft: '12px',
                  paddingRight: '32px !important',
                },
                '& .MuiSvgIcon-root': {
                  color: '#9ca3af',
                  right: '8px',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    backgroundColor: enableDarkTheme
                      ? palette.boxBackground
                      : palette.boxBackground,
                    color: fontColor,
                  },
                },
              }}
            >
              <MenuItem
                value="all"
                sx={{
                  fontSize: '14px',
                }}
              >
                {t('all')}
              </MenuItem>
              <MenuItem
                value="active"
                sx={{
                  fontSize: '14px',
                }}
              >
                {t('active')}
              </MenuItem>
              <MenuItem
                value="info"
                sx={{
                  fontSize: '14px',
                }}
              >
                {t('info')}
              </MenuItem>
              <MenuItem
                value="solved"
                sx={{
                  fontSize: '14px',
                }}
              >
                {t('solved')}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      <Box>
        {dataToDisplay.length === 0 ? (
          <Typography
            sx={{
              fontSize: '12px',
              color: fontColor,
              padding: '10px',
            }}
          >
            {t('no_notifications_available_2')}
          </Typography>
        ) : (
          <NotificationsTable
            data={dataToDisplay}
            suspendNotification={suspendNotification}
            customerId={customerId}
            makeModelDetails={makeModelDetails}
            tirePatterns={tirePatterns}
          />
        )}
      </Box>
    </Box>
  );
};

export default TotalNotification;
